// Audio files picked for upload: recognising them, reading their length, and
// guessing when the recording began (for an uploaded phone recording).

/** The same extensions the server accepts (server/utils/upload.ts AUDIO_EXTS). */
export const AUDIO_EXTS = ['mp3', 'm4a', 'aac', 'wav', 'wave', 'ogg', 'oga', 'opus', 'webm', 'weba',
  'amr', '3gp', '3gpp', '3ga', 'flac', 'wma', 'caf', 'aif', 'aiff', 'mka'];

/** For a file input's `accept`: any audio type, and the phone formats browsers don't always type. */
export const AUDIO_ACCEPT = ['audio/*', ...AUDIO_EXTS.map(e => `.${e}`)].join(',');

const extOf = (name: string) => (name.match(/\.([^.]+)$/)?.[1] || '').toLowerCase();

export function isAudioFile(f: { name: string; type: string }): boolean {
  const type = (f.type || '').toLowerCase();
  if (type.startsWith('audio/')) return true;
  if (type === 'video/3gpp' || type === 'video/3gpp2') return extOf(f.name) !== 'mp4';
  return (!type || type === 'application/octet-stream') && AUDIO_EXTS.includes(extOf(f.name));
}

/** Same rule for a stored document (its MIME type and file name). */
export function isAudioDoc(d: { name: string; mime_type?: string | null }): boolean {
  return isAudioFile({ name: d.name, type: d.mime_type || '' });
}

/** The recording's length in seconds, as the browser reads it; null when it can't (some AMR/3GP files). */
export function readAudioDuration(file: File, timeoutMs = 5000): Promise<number | null> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    let done = false;
    const finish = (v: number | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      audio.removeAttribute('src');
      audio.load();
      URL.revokeObjectURL(url);
      resolve(v != null && Number.isFinite(v) && v > 0 ? v : null);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      // WebM from MediaRecorder reports Infinity until the end is sought.
      if (audio.duration === Infinity) {
        audio.ontimeupdate = () => { audio.ontimeupdate = null; finish(audio.duration); };
        audio.currentTime = 1e9;
      } else finish(audio.duration);
    };
    audio.onerror = () => finish(null);
    audio.src = url;
  });
}

/**
 * When the recording began. Phone recorders mostly put the start in the file
 * name ("Recording_20261030_143012.m4a", "2026-10-30 14.30.12.m4a"); failing
 * that, the file's last change is when the recording stopped, so its length
 * is taken off. `from` says which, so the dialog can say how sure it is.
 */
export function guessRecordingStart(file: File, durationSeconds: number | null): { at: Date; from: 'name' | 'file' } {
  const m = file.name.match(/(20\d{2})[-_.]?(0[1-9]|1[0-2])[-_.]?(0[1-9]|[12]\d|3[01])(?:[ _T-]*(?:at\s*)?([01]\d|2[0-3])[-_.:h]?([0-5]\d)(?:[-_.:m]?([0-5]\d))?)?/i);
  if (m && m[4]) {
    const at = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], m[6] ? +m[6] : 0);
    if (!Number.isNaN(at.getTime()) && at.getTime() <= Date.now() + 60_000) return { at, from: 'name' };
  }
  const end = file.lastModified ? new Date(file.lastModified) : new Date();
  return { at: new Date(end.getTime() - (durationSeconds ?? 0) * 1000), from: 'file' };
}

/** "1 h 05", "12 min", "45 s". */
export function formatAudioLength(seconds: number): string {
  const s = Math.round(seconds);
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
}
